/**
 * Lightweight i18n for the POPSTAR shell — a real, working language switch (the
 * old Settings dropdown was cosmetic: it neither persisted nor changed any text).
 *
 * Design: a tiny React context exposing the current {@link Lang}, a `setLang`
 * mutator (persisted to localStorage so the choice survives reloads), and a `t(key,
 * fallback?)` lookup. Translations live in {@link DICT}; ENGLISH is the canonical
 * key set and the universal fallback, so a key missing from another language simply
 * renders English rather than a blank. Adding a language = adding one entry to
 * {@link LANGS} + a `DICT` map; adding a string = adding one key to `en` (and,
 * optionally, the other languages).
 *
 * SCOPE: this is real infrastructure with the highest-visibility surfaces (menu,
 * top nav, game-over, settings) translated into all six languages. Strings not yet
 * keyed fall back to English — translating the long tail is a content task, not a
 * code one. The choice is ALSO best-effort synced to the account (`users/{uid}.
 * language`, self-writable) by SettingsScreen so it can travel across devices.
 *
 * Presentational/human-timescale only — no game/Firebase imports.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/** The supported UI languages (English-first). */
export type Lang = 'en' | 'es' | 'fr' | 'de' | 'pt' | 'ja';

/** Languages in picker order: code → native label. */
export const LANGS: ReadonlyArray<{ code: Lang; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pt', label: 'Português' },
  { code: 'ja', label: '日本語' },
];

const STORAGE_KEY = 'barrage.lang';

type Dict = Record<string, string>;

/** English — the canonical key set + the universal fallback. */
const en: Dict = {
  'nav.home': 'Home',
  'nav.profile': 'Profile',
  'nav.stickers': 'Stickers',
  'nav.stats': 'Stats',
  'nav.live': 'LIVE',
  'common.back': 'Back',
  'common.menu': 'Menu',
  'common.restart': 'Restart',
  'menu.dragToStart': 'Drag to start',
  'menu.tagline': 'Drag the ball to start. Avoid everything.',
  'menu.playersOnline': 'players online',
  'menu.yourBest': 'your best',
  'menu.leaderboard': 'Leaderboard',
  'menu.popstarSub': 'Profile · stickers · wheels · stats',
  'menu.login': 'Log in',
  'menu.viaPopstar': 'via POPSTAR',
  'gameover.title': 'Game Over',
  'gameover.newBest': '🏆 New personal best!',
  'gameover.thisRun': 'this run',
  'gameover.allTimeHigh': 'all-time high',
  'gameover.watchReplay': '▶ Watch replay',
  'gameover.saveReplay': '💾 Save replay',
  'gameover.levelUp': 'LEVEL UP!',
  'gameover.spinWheel': '🎡 Spin the Prize Wheel',
  'gameover.achievementUnlocked': '🏅 Achievement unlocked!',
  'popstar.exitGame': '‹ Game',
  'profile.dashboard': 'Dashboard',
  'profile.myReplays': '🎬 My Replays',
  'profile.myCard': 'My Card',
  'profile.viewAllReplays': '🎬 View all my saved replays',
  'stickers.management': '🎟️ Management',
  'stickers.prizeWheel': '🎡 Prize Wheel',
  'stickers.seasonal': '🎉 Seasonal',
  'stats.network': '🌐 Network',
  'stats.personal': '🙂 Personal',
  'stats.achievements': '🏅 Achievements',
  'stats.game': 'Game',
  'settings.title': 'Settings',
  'settings.account': 'Account',
  'settings.displayName': 'Display name',
  'settings.email': 'Email',
  'settings.password': 'Password',
  'settings.sendReset': 'Send password reset link',
  'settings.language': 'Language',
  'settings.uiLanguage': 'UI language',
  'settings.langHint': 'Your preferred language for the POPSTAR network.',
  'settings.logout': 'Log out',
  // --- extended surfaces (Stats / Profile / Replays / Card / Seasonal / News / LIVE / tease / shop) ---
  'common.retry': 'Retry',
  'common.loading': 'Loading…',
  'common.watch': '▶ Watch',
  'family.walls': 'Walls',
  'family.lasers': 'Lasers',
  'family.turret': 'Turret',
  'family.caro': 'Caro',
  'family.missiles': 'Missiles',
  'family.bombs': 'Bombs',
  'stats.tagline': 'Your survival record, and where the barrage gets you most.',
  'stats.survival': 'Survival',
  'stats.bestScore': 'Best score',
  'stats.runs': 'Runs',
  'stats.avgScore': 'Avg score',
  'stats.longestRun': 'Longest run',
  'stats.totalAvoided': 'Total avoided',
  'stats.deathsByAttack': 'Deaths by attack',
  'stats.noDeaths': 'No deaths recorded yet — go get hit!',
  'stats.deaths': 'deaths',
  'net.heading': 'Across the network',
  'net.sub': 'How the whole community is doing right now.',
  'net.recentAverage': 'Recent average',
  'net.playersOnline': 'Players online',
  'net.totalRuns': 'Total runs',
  'net.networkBest': 'Network best',
  'net.playersTracked': 'Players tracked',
  'net.loading': 'Loading the network…',
  'ach.heading': 'Achievements',
  'ach.earned': 'earned',
  'ach.unlocked': 'Unlocked',
  'ach.locked': '🔒 Locked',
  'ach.loading': 'Loading achievements…',
  'ach.has1': 'player has this',
  'ach.hasN': 'players have this',
  'ach.survivor.name': 'Survivor',
  'ach.survivor.desc': 'Reach a score of 2,500 in a single run.',
  'ach.veteran.name': 'Veteran',
  'ach.veteran.desc': 'Reach a score of 7,500 in a single run.',
  'ach.legend.name': 'Legend',
  'ach.legend.desc': 'Reach a score of 15,000 in a single run.',
  'seasonal.title': 'Seasonal Event',
  'seasonal.checking': 'Checking for live events…',
  'seasonal.loginToClaim': 'Log in to claim seasonal prizes.',
  'seasonal.noEventTitle': 'No event running right now',
  'seasonal.noEventBody':
    'This lights up when a limited-time event drops — check back to claim a free prize.',
  'seasonal.claimLabel': 'Claim',
  'seasonal.claimPrize': '🎁 Claim prize',
  'seasonal.claiming': 'Claiming…',
  'seasonal.alreadyClaimed': 'You already claimed this event.',
  'seasonal.claimed': 'Prize claimed!',
  'unit.tokens': 'tokens',
  'unit.xp': 'XP',
  'seasonal.aSticker': 'a limited sticker',
  'profile.title': 'Profile',
  'profile.loading': 'Loading your profile…',
  'profile.equippedSticker': 'Equipped Sticker',
  'profile.collection': 'Collection',
  'profile.skill': 'Skill',
  'profile.seasonalCount': 'Seasonal',
  'profile.secret': 'Secret',
  'profile.featuredReplay': 'Featured Replay',
  'profile.featuredSub': 'The starred run others watch on your card.',
  'profile.noEquipped': 'No sticker equipped',
  'profile.starToFeature': 'Star a run to feature it here.',
  'replays.title': 'My Replays',
  'replays.tagline': 'Re-watch your runs. Star your best for your showcase card.',
  'replays.starredLabel': 'starred',
  'replays.tierSuffix': 'tier',
  'replays.loading': 'Loading your replays…',
  'replays.empty': "No replays yet — play a run and it'll show up here.",
  'replays.star': '☆ Star',
  'replays.starred2': '★ Starred',
  'card.title': 'Showcase',
  'card.kicker': 'POPSTAR Showcase',
  'card.equippedSticker': 'Equipped sticker',
  'card.featuredRun': 'Featured run',
  'card.noEquipped': 'No sticker equipped yet',
  'card.noRunSuffix': "hasn't starred a run yet.",
  'wheel.title': 'Prize Wheels',
  'wheel.freeSpin': 'Free Spin',
  'wheel.spinning': 'Spinning…',
  'wheel.youWon': 'You won',
  'wheel.balance': 'Balance',
  'news.title': 'News',
  'news.intro': "The latest from the barrage — events, drops & what's next.",
  'news.empty': 'No news yet',
  'news.emptyBody': 'The feed is quiet for now — check back soon.',
  'live.title': 'POPSTAR LIVE',
  'live.sub': 'Coming soon — replays of recent runs, played back on stage.',
  'live.onAir': 'On air soon',
  'tease.headline': 'Log in to collect',
  'tease.sub':
    "You're playing as a guest. Create a free account to unlock the full POPSTAR experience — and keep your high score.",
  'tease.cta': 'Log in / Sign up',
  'tease.unlock': "What you'll unlock",
  'shop.title': 'Sticker Shop',
  'shop.regular': 'Regular',
  'shop.seasonalType': 'Seasonal',
  'shop.secret': 'Secret',
  'shop.all': 'All',
  'shop.collected': 'collected',
  'shop.owned': 'Owned',
  'shop.buy': 'Buy',
};

const es: Dict = {
  'nav.home': 'Inicio',
  'nav.profile': 'Perfil',
  'nav.stickers': 'Pegatinas',
  'nav.stats': 'Estadísticas',
  'nav.live': 'EN VIVO',
  'common.back': 'Atrás',
  'common.menu': 'Menú',
  'common.restart': 'Reiniciar',
  'menu.dragToStart': 'Arrastra para empezar',
  'menu.tagline': 'Arrastra la bola para empezar. Esquiva todo.',
  'menu.playersOnline': 'jugadores en línea',
  'menu.yourBest': 'tu mejor',
  'menu.leaderboard': 'Clasificación',
  'menu.popstarSub': 'Perfil · pegatinas · ruletas · estadísticas',
  'menu.login': 'Iniciar sesión',
  'menu.viaPopstar': 'con POPSTAR',
  'gameover.title': 'Fin del juego',
  'gameover.newBest': '🏆 ¡Nuevo récord personal!',
  'gameover.thisRun': 'esta partida',
  'gameover.allTimeHigh': 'récord histórico',
  'gameover.watchReplay': '▶ Ver repetición',
  'gameover.saveReplay': '💾 Guardar repetición',
  'gameover.levelUp': '¡SUBISTE DE NIVEL!',
  'gameover.spinWheel': '🎡 Gira la ruleta',
  'gameover.achievementUnlocked': '🏅 ¡Logro desbloqueado!',
  'popstar.exitGame': '‹ Juego',
  'profile.dashboard': 'Panel',
  'profile.myReplays': '🎬 Mis repeticiones',
  'profile.myCard': 'Mi tarjeta',
  'profile.viewAllReplays': '🎬 Ver todas mis repeticiones',
  'stickers.management': '🎟️ Gestión',
  'stickers.prizeWheel': '🎡 Ruleta',
  'stickers.seasonal': '🎉 Temporada',
  'stats.network': '🌐 Red',
  'stats.personal': '🙂 Personal',
  'stats.achievements': '🏅 Logros',
  'stats.game': 'Juego',
  'settings.title': 'Ajustes',
  'settings.account': 'Cuenta',
  'settings.displayName': 'Nombre visible',
  'settings.email': 'Correo',
  'settings.password': 'Contraseña',
  'settings.sendReset': 'Enviar enlace para restablecer',
  'settings.language': 'Idioma',
  'settings.uiLanguage': 'Idioma de la interfaz',
  'settings.langHint': 'Tu idioma preferido para la red POPSTAR.',
  'settings.logout': 'Cerrar sesión',
  "common.retry": "Reintentar",
  "common.loading": "Cargando…",
  "common.watch": "▶ Ver",
  "family.walls": "Muros",
  "family.lasers": "Láseres",
  "family.turret": "Torreta",
  "family.caro": "Caro",
  "family.missiles": "Misiles",
  "family.bombs": "Bombas",
  "stats.tagline": "Tu récord de supervivencia y dónde la barrera te alcanza más.",
  "stats.survival": "Supervivencia",
  "stats.bestScore": "Mejor puntuación",
  "stats.runs": "Partidas",
  "stats.avgScore": "Puntuación media",
  "stats.longestRun": "Partida más larga",
  "stats.totalAvoided": "Total esquivado",
  "stats.deathsByAttack": "Muertes por ataque",
  "stats.noDeaths": "Aún no hay muertes registradas: ¡ve a que te den!",
  "stats.deaths": "muertes",
  "net.heading": "En toda la red",
  "net.sub": "Cómo le va a toda la comunidad ahora mismo.",
  "net.recentAverage": "Media reciente",
  "net.playersOnline": "Jugadores en línea",
  "net.totalRuns": "Partidas totales",
  "net.networkBest": "Mejor de la red",
  "net.playersTracked": "Jugadores registrados",
  "net.loading": "Cargando la red…",
  "ach.heading": "Logros",
  "ach.earned": "conseguidos",
  "ach.unlocked": "Desbloqueado",
  "ach.locked": "🔒 Bloqueado",
  "ach.loading": "Cargando logros…",
  "ach.has1": "jugador lo tiene",
  "ach.hasN": "jugadores lo tienen",
  "ach.survivor.name": "Superviviente",
  "ach.survivor.desc": "Alcanza una puntuación de 2.500 en una sola partida.",
  "ach.veteran.name": "Veterano",
  "ach.veteran.desc": "Alcanza una puntuación de 7.500 en una sola partida.",
  "ach.legend.name": "Leyenda",
  "ach.legend.desc": "Alcanza una puntuación de 15.000 en una sola partida.",
  "seasonal.title": "Evento de temporada",
  "seasonal.checking": "Buscando eventos en directo…",
  "seasonal.loginToClaim": "Inicia sesión para reclamar premios de temporada.",
  "seasonal.noEventTitle": "No hay ningún evento ahora mismo",
  "seasonal.noEventBody": "Esto se activa cuando llega un evento por tiempo limitado: vuelve para reclamar un premio gratis.",
  "seasonal.claimLabel": "Reclamar",
  "seasonal.claimPrize": "🎁 Reclamar premio",
  "seasonal.claiming": "Reclamando…",
  "seasonal.alreadyClaimed": "Ya reclamaste este evento.",
  "seasonal.claimed": "¡Premio reclamado!",
  "unit.tokens": "fichas",
  "unit.xp": "XP",
  "seasonal.aSticker": "un sticker limitado",
  "profile.title": "Perfil",
  "profile.loading": "Cargando tu perfil…",
  "profile.equippedSticker": "Sticker equipado",
  "profile.collection": "Colección",
  "profile.skill": "Habilidad",
  "profile.seasonalCount": "Temporada",
  "profile.secret": "Secreto",
  "profile.featuredReplay": "Repetición destacada",
  "profile.featuredSub": "La partida marcada que otros ven en tu tarjeta.",
  "profile.noEquipped": "Ningún sticker equipado",
  "profile.starToFeature": "Marca una partida para destacarla aquí.",
  "replays.title": "Mis repeticiones",
  "replays.tagline": "Vuelve a ver tus partidas. Marca la mejor para tu tarjeta.",
  "replays.starredLabel": "marcadas",
  "replays.tierSuffix": "nivel",
  "replays.loading": "Cargando tus repeticiones…",
  "replays.empty": "Aún no hay repeticiones: juega una partida y aparecerá aquí.",
  "replays.star": "☆ Marcar",
  "replays.starred2": "★ Marcada",
  "card.title": "Vitrina",
  "card.kicker": "Vitrina POPSTAR",
  "card.equippedSticker": "Sticker equipado",
  "card.featuredRun": "Partida destacada",
  "card.noEquipped": "Aún no hay sticker equipado",
  "card.noRunSuffix": "aún no ha marcado ninguna partida.",
  "wheel.title": "Ruletas de premios",
  "wheel.freeSpin": "Giro gratis",
  "wheel.spinning": "Girando…",
  "wheel.youWon": "Ganaste",
  "wheel.balance": "Saldo",
  "news.title": "Novedades",
  "news.intro": "Lo último de la barrera: eventos, lanzamientos y lo que viene.",
  "news.empty": "Aún no hay novedades",
  "news.emptyBody": "El feed está tranquilo por ahora: vuelve pronto.",
  "live.title": "POPSTAR LIVE",
  "live.sub": "Próximamente: repeticiones de partidas recientes, reproducidas en el escenario.",
  "live.onAir": "Pronto en directo",
  "tease.headline": "Inicia sesión para coleccionar",
  "tease.sub": "Estás jugando como invitado. Crea una cuenta gratis para desbloquear toda la experiencia POPSTAR y conservar tu récord.",
  "tease.cta": "Iniciar sesión / Registrarse",
  "tease.unlock": "Lo que desbloquearás",
  "shop.title": "Tienda de stickers",
  "shop.regular": "Normal",
  "shop.seasonalType": "Temporada",
  "shop.secret": "Secreto",
  "shop.all": "Todos",
  "shop.collected": "coleccionados",
  "shop.owned": "Obtenido",
  "shop.buy": "Comprar",
};

const fr: Dict = {
  'nav.home': 'Accueil',
  'nav.profile': 'Profil',
  'nav.stickers': 'Autocollants',
  'nav.stats': 'Stats',
  'nav.live': 'LIVE',
  'common.back': 'Retour',
  'common.menu': 'Menu',
  'common.restart': 'Recommencer',
  'menu.dragToStart': 'Glissez pour commencer',
  'menu.tagline': 'Glissez la balle pour commencer. Évitez tout.',
  'menu.playersOnline': 'joueurs en ligne',
  'menu.yourBest': 'votre record',
  'menu.leaderboard': 'Classement',
  'menu.popstarSub': 'Profil · autocollants · roues · stats',
  'menu.login': 'Se connecter',
  'menu.viaPopstar': 'via POPSTAR',
  'gameover.title': 'Partie terminée',
  'gameover.newBest': '🏆 Nouveau record personnel !',
  'gameover.thisRun': 'cette partie',
  'gameover.allTimeHigh': 'record absolu',
  'gameover.watchReplay': '▶ Voir le replay',
  'gameover.saveReplay': '💾 Enregistrer le replay',
  'gameover.levelUp': 'NIVEAU SUPÉRIEUR !',
  'gameover.spinWheel': '🎡 Tourner la roue',
  'gameover.achievementUnlocked': '🏅 Succès débloqué !',
  'popstar.exitGame': '‹ Jeu',
  'profile.dashboard': 'Tableau de bord',
  'profile.myReplays': '🎬 Mes replays',
  'profile.myCard': 'Ma carte',
  'profile.viewAllReplays': '🎬 Voir tous mes replays',
  'stickers.management': '🎟️ Gestion',
  'stickers.prizeWheel': '🎡 Roue',
  'stickers.seasonal': '🎉 Saison',
  'stats.network': '🌐 Réseau',
  'stats.personal': '🙂 Personnel',
  'stats.achievements': '🏅 Succès',
  'stats.game': 'Jeu',
  'settings.title': 'Paramètres',
  'settings.account': 'Compte',
  'settings.displayName': 'Nom affiché',
  'settings.email': 'E-mail',
  'settings.password': 'Mot de passe',
  'settings.sendReset': 'Envoyer le lien de réinitialisation',
  'settings.language': 'Langue',
  'settings.uiLanguage': "Langue de l'interface",
  'settings.langHint': 'Votre langue préférée pour le réseau POPSTAR.',
  'settings.logout': 'Se déconnecter',
  "common.retry": "Réessayer",
  "common.loading": "Chargement…",
  "common.watch": "▶ Regarder",
  "family.walls": "Murs",
  "family.lasers": "Lasers",
  "family.turret": "Tourelle",
  "family.caro": "Caro",
  "family.missiles": "Missiles",
  "family.bombs": "Bombes",
  "stats.tagline": "Ton record de survie, et là où le barrage te frappe le plus.",
  "stats.survival": "Survie",
  "stats.bestScore": "Meilleur score",
  "stats.runs": "Parties",
  "stats.avgScore": "Score moyen",
  "stats.longestRun": "Plus longue partie",
  "stats.totalAvoided": "Total esquivé",
  "stats.deathsByAttack": "Morts par attaque",
  "stats.noDeaths": "Aucune mort enregistrée — fais-toi toucher !",
  "stats.deaths": "morts",
  "net.heading": "Sur le réseau",
  "net.sub": "Comment va toute la communauté en ce moment.",
  "net.recentAverage": "Moyenne récente",
  "net.playersOnline": "Joueurs en ligne",
  "net.totalRuns": "Parties totales",
  "net.networkBest": "Record du réseau",
  "net.playersTracked": "Joueurs suivis",
  "net.loading": "Chargement du réseau…",
  "ach.heading": "Succès",
  "ach.earned": "obtenus",
  "ach.unlocked": "Débloqué",
  "ach.locked": "🔒 Verrouillé",
  "ach.loading": "Chargement des succès…",
  "ach.has1": "joueur l'a",
  "ach.hasN": "joueurs l'ont",
  "ach.survivor.name": "Survivant",
  "ach.survivor.desc": "Atteins un score de 2 500 en une seule partie.",
  "ach.veteran.name": "Vétéran",
  "ach.veteran.desc": "Atteins un score de 7 500 en une seule partie.",
  "ach.legend.name": "Légende",
  "ach.legend.desc": "Atteins un score de 15 000 en une seule partie.",
  "seasonal.title": "Événement saisonnier",
  "seasonal.checking": "Recherche d'événements en cours…",
  "seasonal.loginToClaim": "Connecte-toi pour réclamer les prix saisonniers.",
  "seasonal.noEventTitle": "Aucun événement en cours",
  "seasonal.noEventBody": "Ça s'allume quand un événement à durée limitée arrive — reviens réclamer un prix gratuit.",
  "seasonal.claimLabel": "Réclamer",
  "seasonal.claimPrize": "🎁 Réclamer le prix",
  "seasonal.claiming": "Réclamation…",
  "seasonal.alreadyClaimed": "Tu as déjà réclamé cet événement.",
  "seasonal.claimed": "Prix réclamé !",
  "unit.tokens": "jetons",
  "unit.xp": "XP",
  "seasonal.aSticker": "un sticker limité",
  "profile.title": "Profil",
  "profile.loading": "Chargement de ton profil…",
  "profile.equippedSticker": "Sticker équipé",
  "profile.collection": "Collection",
  "profile.skill": "Talent",
  "profile.seasonalCount": "Saisonnier",
  "profile.secret": "Secret",
  "profile.featuredReplay": "Replay vedette",
  "profile.featuredSub": "La partie étoilée que les autres voient sur ta carte.",
  "profile.noEquipped": "Aucun sticker équipé",
  "profile.starToFeature": "Étoile une partie pour la mettre en avant ici.",
  "replays.title": "Mes replays",
  "replays.tagline": "Revois tes parties. Étoile tes meilleures pour ta carte vitrine.",
  "replays.starredLabel": "étoilés",
  "replays.tierSuffix": "rang",
  "replays.loading": "Chargement de tes replays…",
  "replays.empty": "Aucun replay — joue une partie et elle apparaîtra ici.",
  "replays.star": "☆ Étoiler",
  "replays.starred2": "★ Étoilé",
  "card.title": "Vitrine",
  "card.kicker": "Vitrine POPSTAR",
  "card.equippedSticker": "Sticker équipé",
  "card.featuredRun": "Partie vedette",
  "card.noEquipped": "Aucun sticker équipé pour l'instant",
  "card.noRunSuffix": "n'a pas encore étoilé de partie.",
  "wheel.title": "Roues à prix",
  "wheel.freeSpin": "Tour gratuit",
  "wheel.spinning": "Rotation…",
  "wheel.youWon": "Tu as gagné",
  "wheel.balance": "Solde",
  "news.title": "Actus",
  "news.intro": "Les dernières nouvelles du barrage — événements, nouveautés et la suite.",
  "news.empty": "Aucune actu",
  "news.emptyBody": "Le fil est calme pour l'instant — reviens bientôt.",
  "live.title": "POPSTAR LIVE",
  "live.sub": "Bientôt disponible — les replays des parties récentes, rejoués sur scène.",
  "live.onAir": "Bientôt à l'antenne",
  "tease.headline": "Connecte-toi pour collectionner",
  "tease.sub": "Tu joues en invité. Crée un compte gratuit pour débloquer toute l'expérience POPSTAR — et garder ton meilleur score.",
  "tease.cta": "Connexion / Inscription",
  "tease.unlock": "Ce que tu débloqueras",
  "shop.title": "Boutique de stickers",
  "shop.regular": "Classique",
  "shop.seasonalType": "Saisonnier",
  "shop.secret": "Secret",
  "shop.all": "Tous",
  "shop.collected": "collectés",
  "shop.owned": "Obtenu",
  "shop.buy": "Acheter",
};

const de: Dict = {
  'nav.home': 'Start',
  'nav.profile': 'Profil',
  'nav.stickers': 'Sticker',
  'nav.stats': 'Statistik',
  'nav.live': 'LIVE',
  'common.back': 'Zurück',
  'common.menu': 'Menü',
  'common.restart': 'Neustart',
  'menu.dragToStart': 'Zum Starten ziehen',
  'menu.tagline': 'Ziehe den Ball zum Starten. Weiche allem aus.',
  'menu.playersOnline': 'Spieler online',
  'menu.yourBest': 'dein Bestwert',
  'menu.leaderboard': 'Bestenliste',
  'menu.popstarSub': 'Profil · Sticker · Glücksräder · Statistik',
  'menu.login': 'Anmelden',
  'menu.viaPopstar': 'über POPSTAR',
  'gameover.title': 'Game Over',
  'gameover.newBest': '🏆 Neuer persönlicher Rekord!',
  'gameover.thisRun': 'dieser Lauf',
  'gameover.allTimeHigh': 'Allzeit-Rekord',
  'gameover.watchReplay': '▶ Wiederholung ansehen',
  'gameover.saveReplay': '💾 Wiederholung speichern',
  'gameover.levelUp': 'LEVELAUFSTIEG!',
  'gameover.spinWheel': '🎡 Glücksrad drehen',
  'gameover.achievementUnlocked': '🏅 Erfolg freigeschaltet!',
  'popstar.exitGame': '‹ Spiel',
  'profile.dashboard': 'Übersicht',
  'profile.myReplays': '🎬 Meine Wiederholungen',
  'profile.myCard': 'Meine Karte',
  'profile.viewAllReplays': '🎬 Alle Wiederholungen ansehen',
  'stickers.management': '🎟️ Verwaltung',
  'stickers.prizeWheel': '🎡 Glücksrad',
  'stickers.seasonal': '🎉 Saison',
  'stats.network': '🌐 Netzwerk',
  'stats.personal': '🙂 Persönlich',
  'stats.achievements': '🏅 Erfolge',
  'stats.game': 'Spiel',
  'settings.title': 'Einstellungen',
  'settings.account': 'Konto',
  'settings.displayName': 'Anzeigename',
  'settings.email': 'E-Mail',
  'settings.password': 'Passwort',
  'settings.sendReset': 'Link zum Zurücksetzen senden',
  'settings.language': 'Sprache',
  'settings.uiLanguage': 'Anzeigesprache',
  'settings.langHint': 'Deine bevorzugte Sprache für das POPSTAR-Netzwerk.',
  'settings.logout': 'Abmelden',
  "common.retry": "Erneut versuchen",
  "common.loading": "Wird geladen…",
  "common.watch": "▶ Ansehen",
  "family.walls": "Wände",
  "family.lasers": "Laser",
  "family.turret": "Geschützturm",
  "family.caro": "Caro",
  "family.missiles": "Raketen",
  "family.bombs": "Bomben",
  "stats.tagline": "Dein Überlebensrekord – und wo dich die Barrage am häufigsten erwischt.",
  "stats.survival": "Überleben",
  "stats.bestScore": "Bestwert",
  "stats.runs": "Runs",
  "stats.avgScore": "Ø-Wert",
  "stats.longestRun": "Längster Run",
  "stats.totalAvoided": "Gesamt ausgewichen",
  "stats.deathsByAttack": "Tode nach Angriff",
  "stats.noDeaths": "Noch keine Tode erfasst – lass dich treffen!",
  "stats.deaths": "Tode",
  "net.heading": "Im gesamten Netzwerk",
  "net.sub": "Wie es der gesamten Community gerade läuft.",
  "net.recentAverage": "Letzter Durchschnitt",
  "net.playersOnline": "Spieler online",
  "net.totalRuns": "Runs gesamt",
  "net.networkBest": "Netzwerk-Bestwert",
  "net.playersTracked": "Erfasste Spieler",
  "net.loading": "Netzwerk wird geladen…",
  "ach.heading": "Erfolge",
  "ach.earned": "erhalten",
  "ach.unlocked": "Freigeschaltet",
  "ach.locked": "🔒 Gesperrt",
  "ach.loading": "Erfolge werden geladen…",
  "ach.has1": "Spieler hat das",
  "ach.hasN": "Spieler haben das",
  "ach.survivor.name": "Überlebender",
  "ach.survivor.desc": "Erreiche 2.500 Punkte in einem Run.",
  "ach.veteran.name": "Veteran",
  "ach.veteran.desc": "Erreiche 7.500 Punkte in einem Run.",
  "ach.legend.name": "Legende",
  "ach.legend.desc": "Erreiche 15.000 Punkte in einem Run.",
  "seasonal.title": "Saison-Event",
  "seasonal.checking": "Suche nach Live-Events…",
  "seasonal.loginToClaim": "Melde dich an, um Saisonpreise abzuholen.",
  "seasonal.noEventTitle": "Gerade läuft kein Event",
  "seasonal.noEventBody": "Hier leuchtet es auf, wenn ein zeitlich begrenztes Event startet – schau vorbei und sichere dir einen Gratispreis.",
  "seasonal.claimLabel": "Abholen",
  "seasonal.claimPrize": "🎁 Preis abholen",
  "seasonal.claiming": "Wird abgeholt…",
  "seasonal.alreadyClaimed": "Du hast dieses Event bereits abgeholt.",
  "seasonal.claimed": "Preis abgeholt!",
  "unit.tokens": "Tokens",
  "unit.xp": "XP",
  "seasonal.aSticker": "ein limitierter Sticker",
  "profile.title": "Profil",
  "profile.loading": "Dein Profil wird geladen…",
  "profile.equippedSticker": "Ausgerüsteter Sticker",
  "profile.collection": "Sammlung",
  "profile.skill": "Können",
  "profile.seasonalCount": "Saisonal",
  "profile.secret": "Geheim",
  "profile.featuredReplay": "Vorgestellter Replay",
  "profile.featuredSub": "Der markierte Run, den andere auf deiner Karte sehen.",
  "profile.noEquipped": "Kein Sticker ausgerüstet",
  "profile.starToFeature": "Markiere einen Run, um ihn hier zu zeigen.",
  "replays.title": "Meine Replays",
  "replays.tagline": "Schau dir deine Runs erneut an. Markiere deinen besten für deine Showcase-Karte.",
  "replays.starredLabel": "markiert",
  "replays.tierSuffix": "Stufe",
  "replays.loading": "Deine Replays werden geladen…",
  "replays.empty": "Noch keine Replays – spiele einen Run und er erscheint hier.",
  "replays.star": "☆ Markieren",
  "replays.starred2": "★ Markiert",
  "card.title": "Showcase",
  "card.kicker": "POPSTAR Showcase",
  "card.equippedSticker": "Ausgerüsteter Sticker",
  "card.featuredRun": "Vorgestellter Run",
  "card.noEquipped": "Noch kein Sticker ausgerüstet",
  "card.noRunSuffix": "hat noch keinen Run markiert.",
  "wheel.title": "Glücksräder",
  "wheel.freeSpin": "Gratisdreh",
  "wheel.spinning": "Dreht…",
  "wheel.youWon": "Du hast gewonnen",
  "wheel.balance": "Guthaben",
  "news.title": "News",
  "news.intro": "Das Neueste aus der Barrage – Events, Drops & was als Nächstes kommt.",
  "news.empty": "Noch keine News",
  "news.emptyBody": "Der Feed ist gerade ruhig – schau bald wieder vorbei.",
  "live.title": "POPSTAR LIVE",
  "live.sub": "Bald verfügbar – Replays aktueller Runs, live auf der Bühne abgespielt.",
  "live.onAir": "Bald auf Sendung",
  "tease.headline": "Melde dich an zum Sammeln",
  "tease.sub": "Du spielst als Gast. Erstelle ein kostenloses Konto, um das volle POPSTAR-Erlebnis freizuschalten – und deinen Highscore zu behalten.",
  "tease.cta": "Anmelden / Registrieren",
  "tease.unlock": "Das schaltest du frei",
  "shop.title": "Sticker-Shop",
  "shop.regular": "Regulär",
  "shop.seasonalType": "Saisonal",
  "shop.secret": "Geheim",
  "shop.all": "Alle",
  "shop.collected": "gesammelt",
  "shop.owned": "Im Besitz",
  "shop.buy": "Kaufen",
};

const pt: Dict = {
  'nav.home': 'Início',
  'nav.profile': 'Perfil',
  'nav.stickers': 'Figurinhas',
  'nav.stats': 'Estatísticas',
  'nav.live': 'AO VIVO',
  'common.back': 'Voltar',
  'common.menu': 'Menu',
  'common.restart': 'Reiniciar',
  'menu.dragToStart': 'Arraste para começar',
  'menu.tagline': 'Arraste a bola para começar. Desvie de tudo.',
  'menu.playersOnline': 'jogadores online',
  'menu.yourBest': 'seu recorde',
  'menu.leaderboard': 'Ranking',
  'menu.popstarSub': 'Perfil · figurinhas · roletas · estatísticas',
  'menu.login': 'Entrar',
  'menu.viaPopstar': 'via POPSTAR',
  'gameover.title': 'Fim de jogo',
  'gameover.newBest': '🏆 Novo recorde pessoal!',
  'gameover.thisRun': 'esta partida',
  'gameover.allTimeHigh': 'recorde geral',
  'gameover.watchReplay': '▶ Ver replay',
  'gameover.saveReplay': '💾 Salvar replay',
  'gameover.levelUp': 'SUBIU DE NÍVEL!',
  'gameover.spinWheel': '🎡 Girar a roleta',
  'gameover.achievementUnlocked': '🏅 Conquista desbloqueada!',
  'popstar.exitGame': '‹ Jogo',
  'profile.dashboard': 'Painel',
  'profile.myReplays': '🎬 Meus replays',
  'profile.myCard': 'Meu cartão',
  'profile.viewAllReplays': '🎬 Ver todos os meus replays',
  'stickers.management': '🎟️ Gerenciar',
  'stickers.prizeWheel': '🎡 Roleta',
  'stickers.seasonal': '🎉 Sazonal',
  'stats.network': '🌐 Rede',
  'stats.personal': '🙂 Pessoal',
  'stats.achievements': '🏅 Conquistas',
  'stats.game': 'Jogo',
  'settings.title': 'Configurações',
  'settings.account': 'Conta',
  'settings.displayName': 'Nome de exibição',
  'settings.email': 'E-mail',
  'settings.password': 'Senha',
  'settings.sendReset': 'Enviar link de redefinição',
  'settings.language': 'Idioma',
  'settings.uiLanguage': 'Idioma da interface',
  'settings.langHint': 'Seu idioma preferido para a rede POPSTAR.',
  'settings.logout': 'Sair',
  "common.retry": "Tentar de novo",
  "common.loading": "Carregando…",
  "common.watch": "▶ Assistir",
  "family.walls": "Paredes",
  "family.lasers": "Lasers",
  "family.turret": "Torreta",
  "family.caro": "Caro",
  "family.missiles": "Mísseis",
  "family.bombs": "Bombas",
  "stats.tagline": "Seu recorde de sobrevivência e onde a barreira mais te pega.",
  "stats.survival": "Sobrevivência",
  "stats.bestScore": "Melhor pontuação",
  "stats.runs": "Partidas",
  "stats.avgScore": "Pontuação média",
  "stats.longestRun": "Partida mais longa",
  "stats.totalAvoided": "Total desviado",
  "stats.deathsByAttack": "Mortes por ataque",
  "stats.noDeaths": "Nenhuma morte registrada ainda — vá tomar um tiro!",
  "stats.deaths": "mortes",
  "net.heading": "Pela rede",
  "net.sub": "Como toda a comunidade está indo agora.",
  "net.recentAverage": "Média recente",
  "net.playersOnline": "Jogadores online",
  "net.totalRuns": "Total de partidas",
  "net.networkBest": "Melhor da rede",
  "net.playersTracked": "Jogadores monitorados",
  "net.loading": "Carregando a rede…",
  "ach.heading": "Conquistas",
  "ach.earned": "obtidas",
  "ach.unlocked": "Desbloqueada",
  "ach.locked": "🔒 Bloqueada",
  "ach.loading": "Carregando conquistas…",
  "ach.has1": "jogador tem isto",
  "ach.hasN": "jogadores têm isto",
  "ach.survivor.name": "Sobrevivente",
  "ach.survivor.desc": "Alcance uma pontuação de 2.500 em uma única partida.",
  "ach.veteran.name": "Veterano",
  "ach.veteran.desc": "Alcance uma pontuação de 7.500 em uma única partida.",
  "ach.legend.name": "Lenda",
  "ach.legend.desc": "Alcance uma pontuação de 15.000 em uma única partida.",
  "seasonal.title": "Evento Sazonal",
  "seasonal.checking": "Procurando eventos ativos…",
  "seasonal.loginToClaim": "Entre para resgatar prêmios sazonais.",
  "seasonal.noEventTitle": "Nenhum evento ativo agora",
  "seasonal.noEventBody": "Isto acende quando um evento por tempo limitado começa — volte para resgatar um prêmio grátis.",
  "seasonal.claimLabel": "Resgatar",
  "seasonal.claimPrize": "🎁 Resgatar prêmio",
  "seasonal.claiming": "Resgatando…",
  "seasonal.alreadyClaimed": "Você já resgatou este evento.",
  "seasonal.claimed": "Prêmio resgatado!",
  "unit.tokens": "fichas",
  "unit.xp": "XP",
  "seasonal.aSticker": "um adesivo limitado",
  "profile.title": "Perfil",
  "profile.loading": "Carregando seu perfil…",
  "profile.equippedSticker": "Adesivo Equipado",
  "profile.collection": "Coleção",
  "profile.skill": "Habilidade",
  "profile.seasonalCount": "Sazonais",
  "profile.secret": "Secretos",
  "profile.featuredReplay": "Replay em Destaque",
  "profile.featuredSub": "A partida com estrela que aparece no seu cartão.",
  "profile.noEquipped": "Nenhum adesivo equipado",
  "profile.starToFeature": "Marque uma partida com estrela para destacá-la aqui.",
  "replays.title": "Meus Replays",
  "replays.tagline": "Reveja suas partidas. Marque as melhores com estrela para o seu cartão.",
  "replays.starredLabel": "com estrela",
  "replays.tierSuffix": "nível",
  "replays.loading": "Carregando seus replays…",
  "replays.empty": "Nenhum replay ainda — jogue uma partida e ela aparecerá aqui.",
  "replays.star": "☆ Estrela",
  "replays.starred2": "★ Com estrela",
  "card.title": "Vitrine",
  "card.kicker": "Vitrine POPSTAR",
  "card.equippedSticker": "Adesivo equipado",
  "card.featuredRun": "Partida em destaque",
  "card.noEquipped": "Nenhum adesivo equipado ainda",
  "card.noRunSuffix": "ainda não marcou uma partida com estrela.",
  "wheel.title": "Roletas de Prêmios",
  "wheel.freeSpin": "Giro Grátis",
  "wheel.spinning": "Girando…",
  "wheel.youWon": "Você ganhou",
  "wheel.balance": "Saldo",
  "news.title": "Novidades",
  "news.intro": "As últimas da barreira — eventos, lançamentos e o que vem por aí.",
  "news.empty": "Nenhuma novidade ainda",
  "news.emptyBody": "O feed está quieto por enquanto — volte logo.",
  "live.title": "POPSTAR LIVE",
  "live.sub": "Em breve — replays de partidas recentes, exibidos no palco.",
  "live.onAir": "No ar em breve",
  "tease.headline": "Entre para colecionar",
  "tease.sub": "Você está jogando como convidado. Crie uma conta grátis para liberar toda a experiência POPSTAR — e manter seu recorde.",
  "tease.cta": "Entrar / Cadastrar",
  "tease.unlock": "O que você vai liberar",
  "shop.title": "Loja de Adesivos",
  "shop.regular": "Comuns",
  "shop.seasonalType": "Sazonais",
  "shop.secret": "Secretos",
  "shop.all": "Todos",
  "shop.collected": "coletados",
  "shop.owned": "Adquirido",
  "shop.buy": "Comprar",
};

const ja: Dict = {
  'nav.home': 'ホーム',
  'nav.profile': 'プロフィール',
  'nav.stickers': 'ステッカー',
  'nav.stats': '統計',
  'nav.live': 'ライブ',
  'common.back': '戻る',
  'common.menu': 'メニュー',
  'common.restart': 'リスタート',
  'menu.dragToStart': 'ドラッグして開始',
  'menu.tagline': 'ボールをドラッグして開始。すべてを避けよう。',
  'menu.playersOnline': 'オンラインのプレイヤー',
  'menu.yourBest': '自己ベスト',
  'menu.leaderboard': 'ランキング',
  'menu.popstarSub': 'プロフィール・ステッカー・ホイール・統計',
  'menu.login': 'ログイン',
  'menu.viaPopstar': 'POPSTARで',
  'gameover.title': 'ゲームオーバー',
  'gameover.newBest': '🏆 自己ベスト更新！',
  'gameover.thisRun': 'このラン',
  'gameover.allTimeHigh': '歴代最高',
  'gameover.watchReplay': '▶ リプレイを見る',
  'gameover.saveReplay': '💾 リプレイを保存',
  'gameover.levelUp': 'レベルアップ！',
  'gameover.spinWheel': '🎡 ホイールを回す',
  'gameover.achievementUnlocked': '🏅 実績解除！',
  'popstar.exitGame': '‹ ゲーム',
  'profile.dashboard': 'ダッシュボード',
  'profile.myReplays': '🎬 マイリプレイ',
  'profile.myCard': 'マイカード',
  'profile.viewAllReplays': '🎬 保存したリプレイをすべて見る',
  'stickers.management': '🎟️ 管理',
  'stickers.prizeWheel': '🎡 ホイール',
  'stickers.seasonal': '🎉 シーズン',
  'stats.network': '🌐 ネットワーク',
  'stats.personal': '🙂 個人',
  'stats.achievements': '🏅 実績',
  'stats.game': 'ゲーム',
  'settings.title': '設定',
  'settings.account': 'アカウント',
  'settings.displayName': '表示名',
  'settings.email': 'メール',
  'settings.password': 'パスワード',
  'settings.sendReset': 'パスワード再設定リンクを送信',
  'settings.language': '言語',
  'settings.uiLanguage': '表示言語',
  'settings.langHint': 'POPSTARネットワークで使う言語。',
  'settings.logout': 'ログアウト',
  "common.retry": "リトライ",
  "common.loading": "読み込み中…",
  "common.watch": "▶ 見る",
  "family.walls": "ウォール",
  "family.lasers": "レーザー",
  "family.turret": "タレット",
  "family.caro": "カロ",
  "family.missiles": "ミサイル",
  "family.bombs": "ボム",
  "stats.tagline": "あなたの生存記録と、弾幕にやられやすいポイント。",
  "stats.survival": "生存",
  "stats.bestScore": "ベストスコア",
  "stats.runs": "プレイ回数",
  "stats.avgScore": "平均スコア",
  "stats.longestRun": "最長生存",
  "stats.totalAvoided": "回避総数",
  "stats.deathsByAttack": "攻撃別の被弾",
  "stats.noDeaths": "まだ被弾記録なし — 当たりに行こう！",
  "stats.deaths": "回",
  "net.heading": "ネットワーク全体",
  "net.sub": "今、コミュニティ全体の状況。",
  "net.recentAverage": "最近の平均",
  "net.playersOnline": "オンライン人数",
  "net.totalRuns": "総プレイ回数",
  "net.networkBest": "全体ベスト",
  "net.playersTracked": "登録プレイヤー",
  "net.loading": "ネットワークを読み込み中…",
  "ach.heading": "実績",
  "ach.earned": "達成",
  "ach.unlocked": "解除済み",
  "ach.locked": "🔒 ロック中",
  "ach.loading": "実績を読み込み中…",
  "ach.has1": "人が達成",
  "ach.hasN": "人が達成",
  "ach.survivor.name": "サバイバー",
  "ach.survivor.desc": "1回のプレイでスコア2,500に到達。",
  "ach.veteran.name": "ベテラン",
  "ach.veteran.desc": "1回のプレイでスコア7,500に到達。",
  "ach.legend.name": "レジェンド",
  "ach.legend.desc": "1回のプレイでスコア15,000に到達。",
  "seasonal.title": "シーズンイベント",
  "seasonal.checking": "開催中のイベントを確認中…",
  "seasonal.loginToClaim": "ログインしてシーズン賞品を受け取ろう。",
  "seasonal.noEventTitle": "現在開催中のイベントはありません",
  "seasonal.noEventBody": "期間限定イベントが始まるとここに表示されます — また来て無料賞品を受け取ろう。",
  "seasonal.claimLabel": "受け取る",
  "seasonal.claimPrize": "🎁 賞品を受け取る",
  "seasonal.claiming": "受け取り中…",
  "seasonal.alreadyClaimed": "このイベントは受け取り済みです。",
  "seasonal.claimed": "賞品を受け取りました！",
  "unit.tokens": "トークン",
  "unit.xp": "XP",
  "seasonal.aSticker": "限定ステッカー",
  "profile.title": "プロフィール",
  "profile.loading": "プロフィールを読み込み中…",
  "profile.equippedSticker": "装備中のステッカー",
  "profile.collection": "コレクション",
  "profile.skill": "スキル",
  "profile.seasonalCount": "シーズン",
  "profile.secret": "シークレット",
  "profile.featuredReplay": "注目のリプレイ",
  "profile.featuredSub": "カードで他の人が見るスター付きプレイ。",
  "profile.noEquipped": "ステッカー未装備",
  "profile.starToFeature": "プレイにスターを付けてここに表示。",
  "replays.title": "マイリプレイ",
  "replays.tagline": "プレイを見返そう。ベストにスターを付けてショーケースカードに。",
  "replays.starredLabel": "スター付き",
  "replays.tierSuffix": "ティア",
  "replays.loading": "リプレイを読み込み中…",
  "replays.empty": "まだリプレイなし — プレイするとここに表示されます。",
  "replays.star": "☆ スター",
  "replays.starred2": "★ スター付き",
  "card.title": "ショーケース",
  "card.kicker": "POPSTAR ショーケース",
  "card.equippedSticker": "装備中のステッカー",
  "card.featuredRun": "注目のプレイ",
  "card.noEquipped": "ステッカー未装備",
  "card.noRunSuffix": "はまだプレイにスターを付けていません。",
  "wheel.title": "プライズホイール",
  "wheel.freeSpin": "無料スピン",
  "wheel.spinning": "回転中…",
  "wheel.youWon": "獲得",
  "wheel.balance": "残高",
  "news.title": "ニュース",
  "news.intro": "弾幕の最新情報 — イベント、新着、これからの予定。",
  "news.empty": "ニュースはまだありません",
  "news.emptyBody": "今のところ更新はありません — またチェックしてね。",
  "live.title": "POPSTAR LIVE",
  "live.sub": "近日公開 — 最近のプレイのリプレイをステージで再生。",
  "live.onAir": "まもなく配信",
  "tease.headline": "ログインして集めよう",
  "tease.sub": "今はゲストでプレイ中。無料アカウントを作成してPOPSTARをフル体験 — ハイスコアも保存できます。",
  "tease.cta": "ログイン / 新規登録",
  "tease.unlock": "解除できる特典",
  "shop.title": "ステッカーショップ",
  "shop.regular": "レギュラー",
  "shop.seasonalType": "シーズン",
  "shop.secret": "シークレット",
  "shop.all": "すべて",
  "shop.collected": "収集済み",
  "shop.owned": "所持済み",
  "shop.buy": "購入",
};

const DICT: Record<Lang, Dict> = { en, es, fr, de, pt, ja };

/** A translation function: `t(key, fallback?)` → translated string (English fallback). */
export type TFunc = (key: string, fallback?: string) => string;

interface I18nValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: TFunc;
}

const I18nContext = createContext<I18nValue | null>(null);

/** Whether a string is one of the supported language codes. */
export function isLang(v: unknown): v is Lang {
  return typeof v === 'string' && LANGS.some((l) => l.code === v);
}

/** Read the persisted language (localStorage), defaulting to English. */
function loadLang(): Lang {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isLang(v) ? v : 'en';
  } catch {
    return 'en';
  }
}

/**
 * Provides the i18n context. `onChange` is invoked whenever the language changes
 * (App wires it to best-effort persist the choice to the account profile).
 */
export function I18nProvider({
  children,
  onChange,
}: {
  children: ReactNode;
  onChange?: (l: Lang) => void;
}) {
  const [lang, setLangState] = useState<Lang>(loadLang);

  const setLang = useCallback(
    (l: Lang) => {
      setLangState(l);
      try {
        window.localStorage.setItem(STORAGE_KEY, l);
      } catch {
        /* private mode / quota — the in-memory choice still applies this session */
      }
      onChange?.(l);
    },
    [onChange],
  );

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback<TFunc>(
    (key, fallback) => DICT[lang]?.[key] ?? en[key] ?? fallback ?? key,
    [lang],
  );

  const value = useMemo<I18nValue>(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Access the full i18n context (lang + setLang + t). Must be under {@link I18nProvider}. */
export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within <I18nProvider>.');
  return ctx;
}

/** Convenience: just the translation function. */
export function useT(): TFunc {
  return useI18n().t;
}
